import React, { useState } from 'react'
import { DraggableSectionProps } from './DragDropSection/DraggableSection'
import {
  CardsSettings,
  getOrderedSectionLabels,
  LayoutSettings,
  SectionLabels,
  StatsCards
} from 'store'
import styles from './LayoutDialog.module.scss'
import { DragDropSection } from './DragDropSection'
import {
  DragDropContextProps,
  DropResult
} from 'react-beautiful-dnd'
import { toJS } from 'mobx'

export function useDraggableLabels<T> (initialSettings: LayoutSettings | CardsSettings): {
  settings: LayoutSettings | CardsSettings,
  orderedLabels: T[],
  onDragEnd: DragDropContextProps['onDragEnd'],
  onCheckboxClick: (label: T) => void
} {
  const [settings, setSettings] = useState(toJS(initialSettings))
  const [orderedLabels, setOrderedLabels] = useState(getOrderedSectionLabels<T>(initialSettings))

  const onDragEnd = (result: DropResult): void => {
    if (!result.destination) return

    const sourceIndex = result.source.index
    const destinationIndex = result.destination.index
    const label = result.draggableId as unknown as T
    const labels = [...orderedLabels]

    const increment = destinationIndex < sourceIndex ? -1 : 1
    for (let k = sourceIndex; k !== destinationIndex; k += increment) {
      labels[k] = labels[k + increment]
    }
    labels[destinationIndex] = label

    const newSettings = { ...settings }
    labels.forEach((label, index) => {
      const labelKey = String(label)
      newSettings[labelKey].position = index
    })

    setOrderedLabels(labels)
    setSettings(newSettings)
  }

  const onCheckboxClick = (label: T): void => {
    const labelKey = String(label)
    setSettings(s => ({
      ...s,
      [labelKey]: {
        ...s[labelKey],
        visible: !s[labelKey].visible
      }
    }))
  }

  return {
    settings,
    orderedLabels,
    onDragEnd,
    onCheckboxClick
  }
}

export const useLayoutSections = (initialLayoutSettings: LayoutSettings, initialCardsSettings: CardsSettings): {
  layoutSections: DraggableSectionProps[],
  onDragEnd: DragDropContextProps['onDragEnd'],
  getSettings: () => { layoutSettings: LayoutSettings, cardsSettings: CardsSettings }
} => {
  const {
    settings: layoutSettings,
    orderedLabels: labels,
    onDragEnd,
    onCheckboxClick
  } = useDraggableLabels<SectionLabels>(initialLayoutSettings)

  const {
    settings: cardsSettings,
    orderedLabels: cardLabels,
    onDragEnd: onCardsDragEnd,
    onCheckboxClick: onCardsCheckboxClick
  } = useDraggableLabels<StatsCards>(initialCardsSettings)

  const layoutSections = labels.map(label => {
    switch (label) {
      case SectionLabels.STATS_CARDS: {
        return {
          label,
          className: styles.cardsContainer,
          isChecked: layoutSettings[label].visible,
          onCheckboxClick: () => console.log('CLICK'),
          children: (
            <DragDropSection
              onDragEnd={onCardsDragEnd}
              direction='horizontal'
              draggableSections={cardLabels.map(cardLabel => ({
                label: cardLabel,
                isChecked: cardsSettings[cardLabel].visible,
                onCheckboxClick: () => onCardsCheckboxClick(cardLabel)
              }))}
              className={styles.cards}
            />
          )
        }
      }
      default: {
        return {
          label,
          isChecked: layoutSettings[label].visible,
          onCheckboxClick: () => onCheckboxClick(label)
        }
      }
    }
  })

  return {
    layoutSections,
    onDragEnd,
    getSettings: () => ({
      layoutSettings: layoutSettings as unknown as LayoutSettings,
      cardsSettings: cardsSettings as unknown as CardsSettings
    })
  }
}
